import { HintAnimation } from '../hint-motion';
import { hline, text } from '../../academy/films/story-scene';
import { abs, C, glass, icon, orbiTemplate } from './scene-kit';
import { CONTENT, HintStory, navPoint, outlinePill, portalShell } from './hint-story';

/**
 * Hinweis F „Gestalte den Chat in deinen Farben“.
 *
 * Didaktik: Hier zählt der Vorher-Nachher-Effekt — Lernende sehen den Chat
 * im LMS, er soll nach der eigenen Hochschule aussehen. WARUM „Dein Chat in
 * deinen Farben — Logo, Farben und Schrift“ → WO Menüpunkt Interface → WAS
 * Primärfarbe anklicken, Hausfarbe wählen, Logo setzen — die Vorschau rechts
 * färbt sich sofort mit → GESCHAFFT der Chat trägt das eigene Design, Orbi
 * (die Chat-Blase) lacht. Links Einstellung, rechts Wirkung: Ursache und
 * Folge stehen nebeneinander.
 */

const BLUE = '#003598';
const BLUE_2 = '#3B82F6';
const BRAND = '#0F766E';
const BRAND_2 = '#14B8A6';

const SWATCH: [number, number] = [CONTENT.x + 188, CONTENT.y + 79];
const PICK: [number, number] = [CONTENT.x + 110 + 10 + 2 * 27 + 11, CONTENT.y + 96 + 22];
const LOGO: [number, number] = [CONTENT.x + 188, CONTENT.y + 117];

const agentsPage =
  `<div data-r="pageA">` + text(20, 12, 300, 'KI-Agenten', 18, 400) + hline(16, 46, 428) + `</div>`;

const row = (y: number, label: string, control: string) => text(20, y + 4, 120, label, 12, 600, C.muted) + control;

/** Zwei Ebenen übereinander: Standard (blau) und Hausfarbe (`<ref>`, blendet ein). */
const dual = (style: string, ref: string, a: string, b: string, inner = '') =>
  `<div style="${style}background:${a};">` +
  `<div data-r="${ref}" style="position:absolute;inset:0;border-radius:inherit;background:${b};opacity:0;"></div>` +
  `<div style="position:relative;height:100%;">${inner}</div></div>`;

const logoMark = (ref: string, size: number) =>
  `<div data-r="${ref}" style="width:${size}px;height:${size}px;border-radius:${size * 0.3}px;opacity:0;background:#fff;display:flex;align-items:center;justify-content:center;font-size:${size * 0.55}px;font-weight:700;color:${BRAND};flex:0 0 auto;">H</div>`;

const palette =
  `<div data-r="pal" style="${abs(110, 96, 150, 44, 'opacity:0;')}">` +
  glass(
    14,
    0.95,
    'inset:0;',
    [BLUE, BLUE_2, BRAND, '#B45309', '#7C3AED']
      .map((c, i) => `<div style="${abs(10 + i * 27, 11, 22, 22, `border-radius:999px;background:${c};box-shadow:inset 0 0 0 2px rgb(255 255 255 / .7);`)}"></div>`)
      .join(''),
  ) +
  `</div>`;

const preview =
  `<div style="${abs(240, 60, 196, 212, 'border-radius:16px;overflow:hidden;background:#fff;box-shadow:0 14px 28px -14px rgb(20 38 70 / .4), 0 0 0 1px rgb(83 109 145 / .12);')}">` +
  dual(
    `${abs(0, 0, 196, 38)}`,
    'hdBrand',
    BLUE,
    BRAND,
    `<div style="height:100%;display:flex;align-items:center;gap:8px;padding:0 12px;">${logoMark('logoHd', 22)}<span style="font-size:12px;font-weight:600;color:#fff;">Statistik-Tutor</span></div>`,
  ) +
  dual(
    `${abs(12, 50, 150, 44, 'border-radius:14px;border-bottom-left-radius:4px;')}`,
    'botBrand',
    BLUE_2,
    BRAND_2,
    `<div style="padding:8px 11px;font-size:11px;line-height:1.4;color:#fff;">Hallo! Womit fangen wir heute an?</div>`,
  ) +
  `<div style="${abs(70, 104, 114, 30, 'border-radius:14px;border-bottom-right-radius:4px;background:#fff;box-shadow:0 0 0 1px rgb(83 109 145 / .2);font-size:11px;color:#1f2937;display:flex;align-items:center;padding:0 10px;box-sizing:border-box;')}">Erklär mir Varianz</div>` +
  dual(`${abs(10, 176, 132, 26, 'border-radius:999px;')}`, 'inBrand', 'rgb(0 53 152 / .08)', 'rgb(15 118 110 / .1)') +
  `<div style="${abs(152, 166, 34, 34)}">${orbiTemplate('prev', 34)}</div>` +
  `</div>`;

const interfacePage =
  `<div data-r="pageB" style="opacity:0;">` +
  text(20, 12, 200, 'Interface', 18, 400) +
  outlinePill(346, 10, 98, 'Hinzufügen') +
  hline(16, 46, 428) +
  row(
    66,
    'Primärfarbe',
    dual(`${abs(160, 66, 56, 26, 'border-radius:999px;box-shadow:inset 0 0 0 2px rgb(255 255 255 / .7);')}`, 'swBrand', BLUE, BRAND) +
      `<div data-r="swGlow" style="${abs(160, 66, 56, 26, 'border-radius:999px;')}"></div>`,
  ) +
  row(
    104,
    'Logo',
    `<div style="${abs(160, 104, 56, 26, 'border-radius:8px;border:1.5px dashed rgb(83 109 145 / .4);display:flex;align-items:center;justify-content:center;gap:4px;')}">` +
      `<div data-r="logoIcon">${icon('image', 15)}</div>` +
      `<div style="position:absolute;inset:0;display:flex;align-items:center;justify-content:center;">${logoMark('logoBox', 20)}</div></div>`,
  ) +
  row(
    142,
    'Schrift',
    `<div style="${abs(130, 142, 86, 26, `border-radius:999px;background:rgb(255 255 255 / .6);display:flex;align-items:center;justify-content:space-between;padding:0 10px;box-sizing:border-box;font-size:11.5px;color:${C.text};`)}">Arial${icon('chevron', 12)}</div>`,
  ) +
  row(180, 'Chat-Blase', `<div style="${abs(146, 180, 70, 26, `border-radius:999px;background:rgb(83 109 145 / .12);display:flex;align-items:center;justify-content:center;font-size:11.5px;font-weight:600;color:${C.muted};`)}">Orbi</div>`) +
  preview +
  palette +
  `</div>`;

export const styleChatAnimation = (): HintAnimation =>
  new HintStory({
    intro: { line1: ['Dein', 'Chat', 'in', 'deinen', 'Farben'], line2: ['Logo,', 'Farben', 'und', 'Schrift'] },
    portal: portalShell({ active: 0, content: agentsPage + interfacePage }),
    tracks: [
      { r: 'nav0', at: [1.45, 1.55], o: [1, 0] },
      { r: 'nav2', at: [1.45, 1.55], o: [0, 1] },
      { r: 'pageA', at: [1.5, 1.62], o: [1, 0] },
      { r: 'pageB', at: [1.6, 1.8], o: [0, 1], y: [8, 0] },
      { r: 'pal', at: [2.08, 2.2, 2.56, 2.66], o: [0, 1, 1, 0], y: [-4, 0, 0, 0] },
      { r: 'swBrand', at: [2.52, 2.7], o: [0, 1] },
      { r: 'hdBrand', at: [2.58, 2.86], o: [0, 1] },
      { r: 'botBrand', at: [2.62, 2.9], o: [0, 1] },
      { r: 'inBrand', at: [2.62, 2.9], o: [0, 1] },
      { r: 'logoIcon', at: [3.0, 3.08], o: [1, 0] },
      { r: 'logoBox', at: [3.04, 3.18], o: [0, 1], s: [0.6, 1] },
      { r: 'logoHd', at: [3.12, 3.3], o: [0, 1], s: [0.6, 1] },
    ],
    glows: [
      { r: 'navGlow2', at: 1.1, dur: 0.45 },
      { r: 'swGlow', at: 1.85, dur: 0.4 },
    ],
    orbis: [{ prefix: 'prev', pop: 3.45, laugh: 3.55 }],
    cursor: {
      at: [0, 1.08, 1.39, 1.5, 1.92, 2.03, 2.42, 2.54, 2.9, 3.02, 3.5, 99],
      x: [300, 300, navPoint(2)[0], navPoint(2)[0], SWATCH[0], SWATCH[0], PICK[0], PICK[0], LOGO[0], LOGO[0], 330, 330],
      y: [250, 250, navPoint(2)[1], navPoint(2)[1], SWATCH[1], SWATCH[1], PICK[1], PICK[1], LOGO[1], LOGO[1], 330, 330],
      clicks: [1.45, 1.98, 2.48, 2.96],
    },
    camera: {
      at: [0, 1.62, 1.88, 3.95, 4.25, 99],
      x: [320, 320, 378, 378, 320, 320],
      y: [176, 176, 196, 196, 176, 176],
      s: [1, 1, 1.22, 1.22, 1, 1],
    },
    still: 3.9,
  });
